import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@core/auth/hooks/useAuth';
import { getModuleInfoList } from './moduleRegistry';

/**
 * SmartRedirect Component
 *
 * Redirects users to their first accessible module's base path.
 * Used as the default landing page after login instead of a generic dashboard.
 *
 * Logic:
 * 1. Get all registered modules sorted by order
 * 2. Filter to only modules the user has permission to access
 * 3. Redirect to the first accessible module's basePath
 * 4. If no modules are accessible, fall back to /dashboard
 *
 * @example
 * // In AppRouter:
 * <Route index element={<SmartRedirect />} />
 */
export const SmartRedirect = () => {
  const { permissions } = useAuth();

  // Get all registered modules
  const allModules = getModuleInfoList();

  // Filter modules based on user permissions (same logic as ModuleSelector)
  const firstAccessibleModule = useMemo(() => {
    const accessible = allModules.filter((module) => {
      // If no permission required, module is accessible
      if (!module.accessPermission) return true;

      // Check if user has any permission that starts with the module prefix
      // e.g., "Fleet." for fleet module
      const modulePrefix = module.accessPermission.split('.')[0] + '.';
      return permissions.some((p) => p.startsWith(modulePrefix));
    });

    return accessible.length > 0 ? accessible[0] : null;
  }, [allModules, permissions]);

  // Redirect to the first accessible module, or fallback to dashboard
  const targetPath = firstAccessibleModule?.basePath ?? '/dashboard';

  return <Navigate to={targetPath} replace />;
};

export default SmartRedirect;

